import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, Injector, OnInit, signal, Signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { FormControlType, FormField, FormGroupComponent } from '@iot-platform/iot-platform-ui';
import { FormsHelpers } from '@iot-platform/iot-platform-utils';
import { Ward } from '@iot-platform/models/oyan';
import { TranslatePipe } from '@ngx-translate/core';
import { get } from 'lodash';
import { map } from 'rxjs';

export interface ThresholdFormModel {
  threshold: number;
}

export interface ThresholdFormDialogData {
  ward: Ward;
}

@Component({
  selector: 'oyan-ui-threshold-form-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    MatButtonModule,
    MatCardModule,
    MatToolbarModule,
    MatIconModule,
    TranslatePipe,
    FormGroupComponent
  ],
  templateUrl: './threshold-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./threshold-form.component.scss']
})
export class ThresholdFormDialogComponent implements OnInit {
  readonly matDialogRef: MatDialogRef<ThresholdFormDialogComponent> = inject(MatDialogRef<ThresholdFormDialogComponent>);
  readonly data: ThresholdFormDialogData = inject(MAT_DIALOG_DATA, { optional: true }) || { ward: null };
  fields: WritableSignal<Partial<FormField>[]> = signal([]);
  form = new FormGroup<Partial<{ threshold: FormControl<number | null> }>>({});
  initialFormState = signal<ThresholdFormModel | null>(null);
  formValueChanges: Signal<Partial<ThresholdFormModel>>;
  canSubmit = computed(() => {
    const initialFormState = this.initialFormState();
    const formValueChanges = this.formValueChanges();
    return (!this.form?.touched && this.form?.valid) || (this.form?.valid && JSON.stringify(formValueChanges) !== JSON.stringify(initialFormState));
  });
  protected readonly injector = inject(Injector);
  protected readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.formValueChanges = toSignal(
      this.form.valueChanges.pipe(
        map(() => this.form?.getRawValue() as ThresholdFormModel),
        takeUntilDestroyed(this.destroyRef)
      )
    );
  }

  ngOnInit(): void {
    this.initializeFields();
    this.initializeFormEffect();
  }

  initializeFields(): void {
    this.fields.set([
      {
        fxFlex: signal('100%'),
        type: FormControlType.NUMBER,
        name: signal('threshold'),
        label: signal('SITES.WARDS.TABLE.ADD_THRESHOLD'),
        required: signal(true),
        min: signal(0)
      }
    ]);
  }

  initializeFormEffect(): void {
    effect(
      () => {
        // Dynamically add the threshold control to the form
        const thresholdValue = get(this.data, ['ward', 'threshold'], 0);
        this.form.addControl('threshold', new FormControl<number | null>(thresholdValue));

        // Set initial form state after control is added
        this.initialFormState.set(this.form.getRawValue() as ThresholdFormModel);
      },
      { injector: this.injector }
    );
  }

  close(): void {
    this.matDialogRef.close();
  }

  save(): void {
    if (FormsHelpers.isFormValid(this.form)) {
      this.matDialogRef.close(this.form.getRawValue().threshold);
    }
  }
}
